import {
	Box,
	Button,
	TextField,
	Typography,
	Stack,
	Chip,
	CircularProgress,
	Avatar,
	Paper,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { formatLocaleShortDateTime } from '../../services/CommonServices/DataFormatters'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import AddIcon from '@mui/icons-material/Add'
import { showErrorToast } from '../../services/CommonServices/alertServices'
import { ChangeOrderEvent } from '../../types/Work/ChangeOrder'
import {
	getChangeOrderHistory,
	addChangeOrderHistoryEntry,
} from '../../services/changeOrderService'
import { OpenAttachmentsButton } from '../attachments/OpenAttachmentsButton'
import { statusOptions } from '../../types/Work/ChangeOrder'
import { ViewAttachmentsPopup } from '../attachments/ViewAttachmentsPopup'
import { deleteAttachment, getAttachment, listAttachments, uploadAttachments } from '../../services/attachmentService'

interface ChangeOrderHistoryProps {
	changeOrderId: number
	currentStatus: string
	surveyFinalized: boolean
	loadChangeOrders: () => Promise<void>
}

export default function ChangeOrderHistory({
	changeOrderId,
	currentStatus,
	surveyFinalized,
	loadChangeOrders,
}: ChangeOrderHistoryProps) {
	const [isAdding, setIsAdding] = useState(false)
	const [newComment, setNewComment] = useState('')
	const [newStatus, setNewStatus] = useState(currentStatus)
	const { projectTimeZone } = useProjectFeaturesContext()
	const [history, setHistory] = useState<ChangeOrderEvent[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [viewingAttachmentId, setViewingAttachmentId] = useState<number | null>(null)

	const loadHistory = async () => {
		try {
			setIsLoading(true)
			const historyData = await getChangeOrderHistory(changeOrderId)
			setHistory(historyData)
			historyData.length === 0 && setIsAdding(true)
		} catch (error: any) {
			showErrorToast(error)
		} finally {
			setIsLoading(false)
		}
	}

	useEffect(() => {
		loadHistory()
	}, [changeOrderId])

	const handleAddEntry = async () => {
		if (newComment.trim()) {
			setIsSubmitting(true)
			await addChangeOrderHistoryEntry(changeOrderId, newStatus, newComment)
			await loadHistory()
			await loadChangeOrders()
			setNewComment('')
			setNewStatus(currentStatus)
			setIsAdding(false)
			setIsSubmitting(false)
		}
	}

	if (isLoading) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
				<CircularProgress />
			</Box>
		)
	}

	return (
		<>
			<Paper variant='outlined' sx={{ p: 2, backgroundColor: 'background.paper' }}>
				<Stack spacing={0}>
					{history.map((entry) => (
						<Box
							key={entry.eventId}
							sx={{
								display: 'flex',
								gap: 2,
								backgroundColor: 'background.paper',
								borderRadius: 1,
								p: 2,
								'&:hover': {
									backgroundColor: 'action.hover',
								},
							}}
						>
							<Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
								{entry.performedBy.charAt(0)}
							</Avatar>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										mb: 0.5,
										flexWrap: 'wrap',
									}}
								>
									<Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
										{entry.performedBy}
									</Typography>
									<Chip
										label={statusOptions[entry.status]}
										color={
											entry.status === 'Approved'
												? 'success'
												: entry.status === 'Rejected'
												? 'error'
												: entry.status === 'InProcess'
												? 'warning'
												: 'default'
										}
										size='small'
										sx={{ height: 20, fontSize: '0.75rem' }}
									/>
									<Box sx={{ ml: 'auto' }}>
										<OpenAttachmentsButton
											selectAttachments={() => {
												setViewingAttachmentId(entry.eventId)
											}}
										/>
									</Box>
									<Typography variant='body2' color='text.secondary'>
										{formatLocaleShortDateTime(entry.createdAt, projectTimeZone ?? 'UTC')}
									</Typography>
								</Box>
								<Typography
									variant='body1'
									sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
								>
									{entry.notes}
								</Typography>
							</Box>
						</Box>
					))}

					{isAdding && !surveyFinalized ? (
						<Paper
							variant='outlined'
							sx={{
								p: 2,
								backgroundColor: 'background.paper',
								'&:hover': {
									backgroundColor: 'action.hover',
								},
							}}
						>
							<Stack spacing={2}>
								<TextField
									select
									value={newStatus || ''}
									onChange={(e) => setNewStatus(e.target.value || currentStatus)}
									fullWidth
									size='small'
									SelectProps={{
										native: true,
									}}
								>
									<option value='' disabled>
										Select a status
									</option>
									{Object.keys(statusOptions).map((status) => (
										<option key={status} value={status}>
											{statusOptions[status]}
										</option>
									))}
								</TextField>
								<TextField
									label='Add a note...'
									multiline
									rows={3}
									value={newComment}
									onChange={(e) => setNewComment(e.target.value)}
									fullWidth
									variant='outlined'
									sx={{
										'& .MuiOutlinedInput-root': {
											backgroundColor: 'background.paper',
										},
									}}
								/>
								<Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
									<Button
										variant='text'
										onClick={() => {
											setIsAdding(false)
											setNewComment('')
											setNewStatus(currentStatus)
										}}
										disabled={isSubmitting}
										sx={{
											color: 'text.secondary',
										}}
									>
										Cancel
									</Button>
									<Button
										variant='contained'
										onClick={handleAddEntry}
										disabled={!newComment.trim() || !newStatus || isSubmitting}
									>
										{isSubmitting ? <CircularProgress size={24} sx={{ m: 0, p: 0 }} /> : 'Post'}
									</Button>
								</Box>
							</Stack>
						</Paper>
					) : !surveyFinalized ? (
						<Button
							startIcon={<AddIcon />}
							onClick={() => setIsAdding(true)}
							variant='outlined'
							fullWidth
							sx={{
								color: 'text.secondary',
								borderColor: 'divider',
								'&:hover': {
									borderColor: 'primary.main',
									color: 'primary.main',
								},
							}}
						>
							Add a note
						</Button>
					) : null}
				</Stack>
			</Paper>
			{viewingAttachmentId && (
				<ViewAttachmentsPopup
					handleClose={() => {
						setViewingAttachmentId(null)
					}}
					open={viewingAttachmentId !== null}
					itemId={viewingAttachmentId}
					attachmentOperations={{
						listAttachments: (id) => listAttachments(id, 'event', null),
						getAttachment: (id) => getAttachment(id, 'event', null),
						uploadAttachment: (files, id, overwrite, newAnnotations, setUploadProgress) =>
							uploadAttachments(files, id, overwrite, 'event', newAnnotations, setUploadProgress),
						deleteAttachment: (id) => deleteAttachment(id, 'event'),
					}}
				/>
			)}
		</>
	)
}
